import {
  Avatar,
  Badge,
  Box,
  Group,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { Info } from 'react-feather';
import { ContentCard } from '../../../components/common/ContentCard';
import type { ReflectionSessionDetails } from '../../../services/api/preSessionReflection';
import { getZoneCode } from '../../../utils/formatters';

const TITLE_COLOR = '#1A1A2E';
const ICON_COLOR = '#6B7280';
const FIELD_BG = '#F9FAFB';
const FIELD_BORDER = '#E5E7EB';

interface Props {
  details: ReflectionSessionDetails;
  /**
   * MED-1146 (AC-B2) — display-aware resolved topic from the payload header
   * (DISPLAY_AWARE_TOPIC_EXPR: display_name → topic_override → snc.topic →
   * topics.name). Replaces the bare `topics.name` (`details.topic_label`) on the
   * "Topic:" line so this surface resolves the topic identically to the header.
   * Null when no topic is set → the line is hidden.
   */
  sessionTopic?: string | null;
  /**
   * MED-1092 — card corner radius. Defaults to 16 to match the other prep
   * cards; the peer-leader launch card passes 16 explicitly.
   */
  radius?: number | string;
}

/**
 * Session Details card (MED-803 AC #11; polished by MED-1117).
 *
 * A static info card (no collapse) on the ContentCard base: header (info icon +
 * "Session Details"), a "Topic: {topic} · {N}/{M} Sessions" subtitle, and the
 * DATE & TIME / DURATION / LEADER / FORMAT field grid, each field in a rounded
 * sub-box (Figma treatment). The leader shows a role badge (COACH / PEER
 * LEADER) when populated.
 *
 * MED-1117 (AC5) — the Zoom-style "Session meeting link" sub-card is dropped:
 * SGOS decoupled the meeting link from Zoom and the join affordance now lives on
 * the live-session / waiting-room views (MED-1112), not the pre-session details.
 * The In-Person location is kept (it's session info, not the dropped element).
 */
export function SessionDetailsCard({ details, sessionTopic, radius = 16 }: Props) {
  const formattedDateTime = formatDateTime(
    details.scheduled_start,
    details.timezone
  );
  // MED-1146 (AC-B2) — prefer the display-aware topic; fall back to the bare
  // topic_label only if the resolved value was not supplied.
  const topicText = sessionTopic ?? details.topic_label;
  return (
    <ContentCard variant="bordered" radius={radius} padding="lg" contentGap={0}>
      <Stack gap="md">
        <Stack gap={2}>
          <Group gap="xs" align="center">
            <Info size={18} color={ICON_COLOR} aria-hidden />
            <Text fw={600} fz={17} c={TITLE_COLOR}>
              Session Details
            </Text>
          </Group>
          {topicText && (
            <Text size="sm" c="dimmed">
              Topic: {topicText}
              {details.session_number != null &&
                details.total_sessions != null && (
                  <>
                    {' '}
                    · {details.session_number}/{details.total_sessions} Sessions
                  </>
                )}
            </Text>
          )}
        </Stack>

        <SimpleGrid cols={2} spacing="sm">
          <FieldTile label="Date & time" value={formattedDateTime} />
          <FieldTile
            label="Duration"
            value={
              details.duration_minutes != null
                ? `${details.duration_minutes} minutes`
                : '—'
            }
          />
          <FieldTile
            label="Leader"
            value={
              details.leader.display_name ? (
                <Group gap="xs" wrap="wrap">
                  <Avatar size="sm" radius="xl" color="medleyGreen">
                    {leaderInitials(details.leader.display_name)}
                  </Avatar>
                  <Text size="sm">{details.leader.display_name}</Text>
                  {details.leader.role_label && (
                    <Badge
                      color={
                        details.leader.role_label === 'PEER LEADER'
                          ? 'grape'
                          : 'medleyGreen'
                      }
                      variant="light"
                      radius="sm"
                      size="sm"
                      styles={{
                        label: {
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          fontSize: 10,
                          letterSpacing: '0.3px',
                        },
                      }}
                    >
                      {details.leader.role_label}
                    </Badge>
                  )}
                </Group>
              ) : (
                '—'
              )
            }
          />
          <FieldTile
            label="Format"
            value={details.format === 'Virtual' ? 'Virtual' : 'In-Person'}
          />
        </SimpleGrid>

        {details.format === 'In-Person' && details.location && (
          <Box
            style={{
              border: `1px solid ${FIELD_BORDER}`,
              borderRadius: 8,
              padding: '10px 12px',
              background: FIELD_BG,
            }}
          >
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={4}>
              Location
            </Text>
            <Text size="sm">{details.location}</Text>
          </Box>
        )}
      </Stack>
    </ContentCard>
  );
}

/** A single field in the details grid — label + value inside a rounded sub-box. */
function FieldTile({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box
      style={{
        border: `1px solid ${FIELD_BORDER}`,
        borderRadius: 8,
        padding: '10px 12px',
        background: FIELD_BG,
      }}
    >
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={4}>
        {label}
      </Text>
      {typeof value === 'string' ? <Text size="sm">{value}</Text> : value}
    </Box>
  );
}

function leaderInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function formatDateTime(iso: string | null, _timezone: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const text = d.toLocaleString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
    // MED-1389: name the zone, as the coach view does.
    return `${text} ${getZoneCode(null, d)}`;
  } catch {
    return iso;
  }
}
